/**
 * Zentrale CSS-Klassen-Strings fuer Formular-Elemente.
 *
 * ── EIN Feldbild, zwei Schreibweisen ───────────────────────────────────────
 *
 * Die Suite schreibt Formularfelder auf zwei Arten: als Komponente (`<Input>`,
 * `<Textarea>`, `<Auswahl>`) und als Klassenkette (`className={inputCls}`). Am
 * 2026-08-20 gezaehlt: 316-mal die Komponente, 157-mal die Konstante — davon 111
 * allein in janeway-automations. Beide Schreibweisen sind in Ordnung; sie duerfen
 * nur nicht VERSCHIEDEN AUSSEHEN.
 *
 * Genau das taten sie, und zwar gegenlaeufig: die Komponenten fuellten mit
 * `bg-background` und umrandeten mit `border-input`, `inputCls` fuellte mit
 * `bg-input` und umrandete mit `border-border`. Im dunklen Modus heisst das
 * schwarz-mit-hellem-Rand neben grau-mit-dunklem-Rand — nebeneinander zwei
 * verschiedene Bauteile. Aufgefallen ist es im Agenten-Editor, der als einzige
 * Maske ausschliesslich die Konstante benutzt (51-mal, kein einziges `<Input>`)
 * und deshalb Auswahlfelder neben Textfeldern in beiden Optiken zeigte.
 *
 * Aufgeloest zugunsten der Komponenten-Optik — sie ist die haeufigere (ausserhalb
 * von automations 316 zu 46) und die des Radix-Bestands (`Select`, `DatePicker`,
 * `Button` bauen darauf). `Input`, `Textarea` und `Auswahl` rendern seit
 * gui-elements 0.266.0 AUS DIESER KONSTANTE, damit es nicht wieder auseinander
 * laeuft.
 */

/** Standard-Input (vollbreite Formularfelder) — Grundlage von `<Input>` und `<Auswahl>`. */
export const inputCls =
  "flex h-9 w-full rounded-[var(--radius)] border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

/** Kompakter Input (Filter-Bars, Tabellen-Header) — dasselbe Bild, eine Stufe kleiner. */
export const inputClsCompact =
  "flex h-8 w-full rounded-[var(--radius)] border border-input bg-background px-2.5 py-1 text-xs shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

/** Standard-Label */
export const labelCls =
  "block text-xs text-muted-foreground mb-1.5 font-medium";

/** Select (identisch mit inputCls) */
export const selectCls = inputCls;

/** Primaer-Button */
export const btnPrimary =
  "px-4 py-2 text-xs font-medium bg-primary text-primary-foreground rounded-[var(--radius)] hover:opacity-90 transition-opacity disabled:opacity-50";

/**
 * Outline-Button.
 *
 * `border-input`, nicht `border-border`: das hier ist der Umriss eines
 * BEDIENELEMENTS ohne eigene Flaeche, und dafuer gilt WCAG 1.4.11 mit 3,0 : 1.
 * `border` sagt nur 1,5 : 1 zu (`CONTRAST_PAIRS`), `input` sagt 3,0 gegen
 * `background` UND `card` zu.
 *
 * Bis 0.468.0 stand hier `border-border`, und der Bedienkanten-Waechter hat es
 * nicht gesehen: er liest literale Klassen im JSX, nicht Klassen aus einer
 * Konstanten. Ueber diese eine Zeile hingen sieben Knoepfe in
 * `PlatformProviderManager` und `WebhookManager` an der falschen Zusage.
 */
export const btnOutline =
  "px-3 py-2 text-xs font-medium border border-input rounded-[var(--radius)] hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50";

/**
 * DIE EINE SPRACHE FUER „AUSGEWAEHLT".
 *
 * Gemessen am 2026-09-20 ueber `src/`: **siebzehn** verschiedene Muster an rund
 * 33 Stellen malen denselben Zustand — sechs Spielarten mit `border-primary`,
 * sieben mit `border-foreground`, eine mit `muted-foreground`, dazu vier ganz
 * ohne Kantenwechsel. Allein im Design-Reiter stehen vier davon nebeneinander.
 * Der Betreiber hat es als „manchmal haben wir halt sone Border-Fehler"
 * gemeldet; es ist kein Fehler je Stelle, sondern das Fehlen einer Regel.
 *
 * Gewaehlt traegt die Akzentkante und eine leise Akzentflaeche, ungewaehlt die
 * Bedienkante. **Beide tragen eine Kante derselben Breite** — sonst verschiebt
 * das Umschalten den Inhalt um ein Pixel.
 *
 * Wer eine Ausnahme braucht, schreibt sie mit ihrem Grund an ihren Ort. Eine
 * zweite Variante hier waere wieder der Anfang von siebzehn.
 */
export const auswahlCls = (aktiv: boolean) =>
  "rounded-[var(--radius)] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  (aktiv
    ? "border-primary bg-primary/15 text-foreground"
    : "border-input text-muted-foreground hover:text-foreground");

/** Card-Container */
export const cardCls =
  "rounded-xl border border-border bg-card p-3 md:p-6";
